<!--
  Visual hierarchy (AGENTS.md §Visual hierarchy)
  Question: "Which ledgers are connected to mine, and what is waiting on my answer?"
  Answer:   proposals this ledger received - the one thing here that needs a decision.
  Context:  this ledger's own code, connected ledgers and sent proposals - state to read.
  Controls: Connect a ledger (a dialog) and the owner's code rotation.
-->
<script setup lang="ts">
import { ActionButton, ErrorState, PageHeader, PageShell } from '@taler/ui'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSession } from '@/app/session'
import {
  canManageRelationships,
  canViewRelationships,
  ConnectLedgerDialog,
  ReceivedProposalsList,
  type RelationshipProposal,
  RelationshipIcon,
  RelationshipsList,
  SentProposalsList,
  ShareCodeCard,
  useConnections,
  useLedgerCode,
  useOutgoingProposals,
  useProposals,
} from '@/features/relationships'

const { t } = useI18n()
const session = useSession()

const canView = computed(() => canViewRelationships(session.can))
const canManage = computed(() => canManageRelationships(session.can))
const activeLedger = computed(() => session.activeLedger.value)
const activeLedgerId = computed(() => activeLedger.value?.id)

const connections = useConnections(() => activeLedgerId.value)
const proposals = useProposals(() => activeLedgerId.value)
const outgoing = useOutgoingProposals(() => activeLedgerId.value)
const ledgerCode = useLedgerCode()
const isConnecting = ref(false)

const receivedProposals = computed(() =>
  proposals.proposals.value.filter(
    (proposal) => proposal.targetLedgerId === activeLedgerId.value && proposal.status === 'PROPOSED',
  ),
)

/** A proposal's other side may be a ledger this principal is not a member of, so its name is unknown. */
function ledgerName(ledgerId: string): string {
  return (
    session.ledgers.value.find((ledger) => ledger.id === ledgerId)?.name ?? t('relationships.unknownLedger')
  )
}

async function onAccept(proposal: RelationshipProposal): Promise<void> {
  await proposals.accept(proposal)
}

async function onDecline(proposal: RelationshipProposal): Promise<void> {
  await proposals.decline(proposal)
}

async function onRotate(): Promise<void> {
  if (activeLedgerId.value === undefined) return
  try {
    await ledgerCode.rotate(activeLedgerId.value)
  } catch {
    // Surfaced through ledgerCode.rotateError.
  }
}
</script>

<template>
  <PageShell class="gap-6">
    <PageHeader :title="t('relationships.title')">
      <template #subtitle>{{ t('relationships.intro') }}</template>
    </PageHeader>

    <p v-if="session.isLoading.value" role="status">{{ t('app.loading') }}</p>
    <p v-else-if="activeLedger === undefined" class="text-[color:var(--color-text-muted)]">
      {{ t('transfers.noLedger') }}
    </p>
    <p v-else-if="!canView" class="text-[color:var(--color-text-muted)]">{{ t('relationships.noAccess') }}</p>

    <template v-else>
      <ShareCodeCard
        :code="activeLedger.shareCode"
        :can-rotate="canManage"
        :is-rotating="ledgerCode.isRotating.value"
        :rotate-failed="ledgerCode.rotateError.value"
        @rotate="onRotate"
      />

      <div v-if="canManage">
        <ActionButton @click="isConnecting = true">
          <RelationshipIcon name="plus" />
          {{ t('relationships.connectCta') }}
        </ActionButton>
        <ConnectLedgerDialog
          :open="isConnecting"
          :source-ledger-id="activeLedger.id"
          :source-ledger-name="activeLedger.name"
          @close="isConnecting = false"
        />
      </div>

      <ReceivedProposalsList
        :proposals="receivedProposals"
        :ledger-name="ledgerName"
        :is-busy="proposals.isBusy"
        :can-respond="canManage"
        @accept="onAccept"
        @decline="onDecline"
      />

      <ErrorState
        v-if="connections.error.value"
        :title="t('relationships.loadFailed')"
        :retry-label="t('relationships.retry')"
        :retrying="connections.retrying.value"
        @retry="connections.retry"
      />
      <RelationshipsList v-else :connections="connections.connections.value" />

      <SentProposalsList :proposals="outgoing.proposals.value" :ledger-name="ledgerName" />
    </template>
  </PageShell>
</template>
